import { Type } from 'class-transformer';
import {
  IsDate,
  IsIn,
  IsObject,
  IsUUID,
  Matches,
  Validate,
  type ValidationArguments,
  ValidatorConstraint,
  type ValidatorConstraintInterface,
} from 'class-validator';
import {
  ANALYTICS_APPS,
  ANALYTICS_EVENT_CATALOGUE,
  ANALYTICS_EVENT_NAMES,
  ANALYTICS_PLATFORMS,
  type AnalyticsApp,
  type AnalyticsEventName,
  type AnalyticsPlatform,
  type AnalyticsPropValues,
  analyticsPropsProblem,
} from './analytics-catalogue';

// Capture's native version, desktop-web's 7-character commit, or a local build's
// placeholder. Short digit groups only, so no field can carry an identifier.
const APP_VERSION_PATTERN =
  /^(\d{1,4}(\.\d{1,4}){1,3}|[0-9a-f]{7}|dev|unknown)$/;

@ValidatorConstraint({ name: 'catalogueProps' })
class CataloguePropsConstraint implements ValidatorConstraintInterface {
  validate(props: unknown, { object }: ValidationArguments): boolean {
    return !analyticsPropsProblem(
      (object as AnalyticsEventFieldsDto).name,
      props,
      ANALYTICS_EVENT_CATALOGUE,
    );
  }

  defaultMessage(): string {
    return 'props must match the event catalogue';
  }
}

/** An event without its name rule, so a narrower event list can declare its own. */
export abstract class AnalyticsEventFieldsDto {
  abstract name: AnalyticsEventName;

  @IsObject()
  @Validate(CataloguePropsConstraint)
  props: AnalyticsPropValues;

  @Type(() => Date)
  @IsDate()
  clientTimestamp: Date;
}

export class AnalyticsEventDto extends AnalyticsEventFieldsDto {
  @IsIn(ANALYTICS_EVENT_NAMES)
  name: AnalyticsEventName;
}

/** What a client sends with every batch, kept as sent all the way to storage. */
export abstract class AnalyticsBatchFieldsDto {
  /** New per batch, and the same on every resend of it, so a repeat stores nothing. */
  @IsUUID()
  batchId: string;

  @IsUUID()
  sessionId: string;

  @IsIn(ANALYTICS_APPS)
  app: AnalyticsApp;

  @Matches(APP_VERSION_PATTERN)
  appVersion: string;

  @IsIn(ANALYTICS_PLATFORMS)
  platform: AnalyticsPlatform;
}
